/**
 * A loading indicator in the shadcn look, per the checklist in
 * agents/CODING_STANDARDS.md -> "Styling: two systems": lucide's Loader2
 * spinning, in three sizes, announced through `role="status"` and an sr-only
 * label (`label`, default "Loading"). Inside a Button the icon takes the
 * button's icon size.
 *
 * LoadingOverlay replaced the MUI Backdrop + CircularProgress pair: a fixed,
 * full-viewport dim at `tw-z-dialog` with a large white spinner centred.
 */
import React from 'react'
import { cva } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'

import { cn } from '../../utils/cn.js'

const spinnerVariants = cva('tw-animate-spin tw-shrink-0', {
  variants: {
    size: {
      sm: 'tw-h-4 tw-w-4',
      default: 'tw-h-6 tw-w-6',
      lg: 'tw-h-10 tw-w-10',
    },
  },
  defaultVariants: {
    size: 'default',
  },
})

const Spinner = React.forwardRef(
  ({ className, size, label = 'Loading', ...props }, ref) => (
    <span
      ref={ref}
      role="status"
      data-slot="spinner"
      className={cn(
        'tw-inline-flex tw-items-center tw-justify-center',
        className,
      )}
      {...props}
    >
      <Loader2
        aria-hidden="true"
        className={spinnerVariants({ size })}
      />
      <span
        data-slot="spinner-label"
        className="tw-sr-only"
      >
        {label}
      </span>
    </span>
  ),
)
Spinner.displayName = 'Spinner'

const LoadingOverlay = React.forwardRef(
  ({ className, label = 'Loading', ...props }, ref) => (
    <div
      ref={ref}
      role="status"
      data-slot="loading-overlay"
      className={cn(
        'tw-fixed tw-inset-0 tw-z-dialog tw-flex tw-items-center tw-justify-center tw-bg-black/50',
        className,
      )}
      {...props}
    >
      <Loader2
        aria-hidden="true"
        className={cn(spinnerVariants({ size: 'lg' }), 'tw-text-white')}
      />
      <span
        data-slot="loading-overlay-label"
        className="tw-sr-only"
      >
        {label}
      </span>
    </div>
  ),
)
LoadingOverlay.displayName = 'LoadingOverlay'

export { Spinner, LoadingOverlay, spinnerVariants }
